export const MODULE_ACCESS_CONFIG = {
  '/time': {
    title: '时间统计',
    memberKey: 'timeUser',
    payKey: 'time',
    valueTitle: '继续记录和分析时间使用情况',
    valueDesc: '时间统计用于记录每天花在不同事情上的时长，并生成统计视图，帮助你回看时间投入是否和目标一致。',
    valuePoints: ['继续新增和编辑时间记录', '查看历史时间记录和统计', '导出或查看已有报表数据'],
  },
  '/bill': {
    title: '剑飞记账',
    memberKey: 'billUser',
    payKey: 'bill',
    valueTitle: '继续管理个人收支和预算',
    valueDesc: '剑飞记账用于记录收入、支出和分类预算，帮助你知道钱花在了哪里，以及本月预算执行情况。',
    valuePoints: ['继续录入收入和支出', '查看历史账单和分类汇总', '继续使用预算管理能力'],
  },
  '/write': {
    title: '语写',
    memberKey: 'writerSummary',
    payKey: 'yuxie',
    valueTitle: '继续写作、沉淀和同步每日文字',
    valueDesc: '语写用于按日期记录文字内容，保留历史版本和云端同步，适合持续写作、复盘和素材沉淀。',
    valuePoints: ['继续新增每日语写内容', '查看和编辑历史版本', '使用云端同步保留写作数据'],
  },
  '/write-article': {
    title: '我的文章',
    memberKey: 'writerSummary',
    payKey: 'yuxie',
    valueTitle: '继续整理已经写下的文章内容',
    valueDesc: '我的文章用于集中查看和整理语写内容，方便把日常记录继续加工成文章。',
    valuePoints: ['继续查看文章列表', '编辑已有文章内容', '把日常记录整理成可阅读文本'],
  },
  '/write-read': {
    title: '剑飞阅读',
    memberKey: 'readUser',
    payKey: 'read',
    valueTitle: '继续记录阅读进度和年度阅读统计',
    valueDesc: '剑飞阅读用于记录每本书的阅读进度、心得和书单状态，帮助你持续维护自己的阅读记录。',
    valuePoints: ['继续记录读书进度和心得', '管理已读、在读、想读书单', '查看年度阅读统计'],
  },
  '/write-live': {
    title: '人生规划',
    memberKey: 'everyBirthdayUser',
    payKey: 'live',
    valueTitle: '继续维护每年的人生轨迹',
    valueDesc: '人生规划按年龄和日期组织长期记录，适合写年度计划、阶段复盘和成长轨迹。',
    valuePoints: ['继续查看每一年的记录页', '维护年度计划和成长记录', '保留已有的人生轨迹数据'],
  },
  '/write-live100': {
    title: '人生百年',
    memberKey: 'live100User',
    payKey: 'live100',
    valueTitle: '继续按百年视角查看和记录人生规划',
    valueDesc: '人生百年用于按更长时间尺度组织人生节点，帮助你把长期目标和年度记录放在一起看。',
    valuePoints: ['继续查看百年时间轴', '维护长期目标和人生节点', '保留已有规划数据'],
  },
  '/write-publish': {
    title: '剑飞出书',
    memberKey: 'publishUser',
    payKey: 'publish',
    valueTitle: '继续把语写成果整理成书稿',
    valueDesc: '剑飞出书用于把语写内容整理为出版结构，支持排版导出和出版对接相关流程。',
    valuePoints: ['继续整理书稿目录和内容', '使用排版导出能力', '保留已有出书项目数据'],
  },
};

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '' && value !== false;
};

const hasActivationSignal = (data, memberKey) => {
  if (!data || typeof data !== 'object') return false;

  if (data.vipLevel && data.vipLevel !== 'Lv0') return true;
  if (data.beginDate) return true;

  if (memberKey === 'writerSummary') {
    return [
      data.days,
      data.totalDays,
      data.totalWordCount,
      data.wordCount,
      data.articleCount,
    ].some(hasValue);
  }

  if (memberKey === 'live100User') {
    return hasValue(data.birthday);
  }

  if (memberKey === 'everyBirthdayUser') {
    return hasValue(data.birthday) || hasValue(data.name);
  }

  if (memberKey === 'publishUser') {
    return hasValue(data.books) || hasValue(data.bookCount);
  }

  return false;
};

export function getModuleAccess(user, modulePath) {
  const config = MODULE_ACCESS_CONFIG[modulePath];

  if (!config) {
    return {
      modulePath,
      status: 'free',
      isAllowed: true,
    };
  }

  const base = {
    ...config,
    modulePath,
    payPath: `/pay/${config.payKey}`,
  };

  if (!(user && user.userNum)) {
    return {
      ...base,
      status: 'anonymous',
      isAllowed: false,
      reason: 'login_required',
      actionText: '立即登录',
    };
  }

  const data = user[config.memberKey];
  if (!data || typeof data !== 'object') {
    return {
      ...base,
      status: 'not_opened',
      isAllowed: false,
      reason: 'not_opened',
      actionText: '立即开通',
    };
  }

  const endTime = parseDate(data.endDate);
  if (endTime && endTime <= Date.now()) {
    return {
      ...base,
      status: 'expired',
      isAllowed: false,
      reason: 'expired',
      actionText: '立即续费',
      startedAt: data.beginDate,
      expiredAt: data.endDate,
      remainingDays: 0,
    };
  }

  if (endTime || hasActivationSignal(data, config.memberKey)) {
    return {
      ...base,
      status: 'active',
      isAllowed: true,
      startedAt: data.beginDate,
      expiredAt: data.endDate,
      remainingDays: endTime ? Math.max(0, Math.ceil((endTime - Date.now()) / DAY_MS)) : null,
    };
  }

  return {
    ...base,
    status: 'not_opened',
    isAllowed: false,
    reason: 'not_opened',
    actionText: '立即开通',
  };
}

export function getModuleAccessByPath(user, pathname) {
  const modulePath = Object.keys(MODULE_ACCESS_CONFIG)
    .sort((a, b) => b.length - a.length)
    .find(path => pathname === path || pathname.startsWith(`${path}/`));

  return getModuleAccess(user, modulePath);
}

export function isModuleAllowed(user, modulePath) {
  return getModuleAccess(user, modulePath).isAllowed;
}
